import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast.service';
import {
  extractDjangoErrors,
  formatDjangoErrors,
} from '../../../../shared/utils/django-errors';
import { ValidationRulesService } from '../../service/validation-rules.service';
import { ValidationRulesApiActions, ValidationRulesUiActions } from '../actions';

/**
 * Map an HttpErrorResponse (or any thrown value) into a user-facing toast.
 * Falls back to ``fallback`` when the server didn't return a structured
 * `IDjangoErrors` body (network errors, 500 HTML, etc).
 */
function toastFor(toast: ToastService, err: unknown, fallback: string): void {
  const body = extractDjangoErrors(err);
  const formatted = formatDjangoErrors(body);
  toast.show(
    formatted && formatted !== 'An unexpected error occurred.' ? formatted : fallback,
    'error',
  );
}

@Injectable()
export class ValidationRulesEffects {
  private actions$ = inject(Actions);
  private toast = inject(ToastService);
  private service = inject(ValidationRulesService);

  // ---- loads ---------------------------------------------------------

  loadFieldRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.LoadFieldRules),
      mergeMap(({ filters }) =>
        this.service.getFieldRules(filters).pipe(
          map((payload) => ValidationRulesApiActions.loadFieldRulesSuccess({ payload })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to load field rules');
            return of(
              ValidationRulesApiActions.loadFieldRulesError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  loadCrossFieldRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.LoadCrossFieldRules),
      mergeMap(({ filters }) =>
        this.service.getCrossFieldRules(filters).pipe(
          map((payload) => ValidationRulesApiActions.loadCrossFieldRulesSuccess({ payload })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to load cross-field rules');
            return of(
              ValidationRulesApiActions.loadCrossFieldRulesError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  loadCatalog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.LoadCatalog),
      mergeMap(() =>
        this.service.getRuleTypeCatalog().pipe(
          map((payload) => ValidationRulesApiActions.loadCatalogSuccess({ payload })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to load rule-type catalog');
            return of(
              ValidationRulesApiActions.loadCatalogError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  // ---- field-rule mutations ----------------------------------------

  createFieldRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.CreateFieldRule),
      mergeMap(({ payload }) =>
        this.service.createFieldRule(payload).pipe(
          tap(() => this.toast.show('Rule created', 'success')),
          map((rule) => ValidationRulesApiActions.createFieldRuleSuccess({ payload: rule })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to create rule');
            return of(
              ValidationRulesApiActions.createFieldRuleError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  patchFieldRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.PatchFieldRule),
      mergeMap(({ id, changes }) =>
        this.service.patchFieldRule(id, changes).pipe(
          tap(() => this.toast.show('Rule updated', 'success')),
          map((rule) => ValidationRulesApiActions.patchFieldRuleSuccess({ payload: rule })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to update rule');
            return of(
              ValidationRulesApiActions.patchFieldRuleError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  deleteFieldRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.DeleteFieldRule),
      mergeMap(({ id }) =>
        this.service.deleteFieldRule(id).pipe(
          tap(() => this.toast.show('Rule deleted', 'success')),
          map(() => ValidationRulesApiActions.deleteFieldRuleSuccess({ id })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to delete rule');
            return of(
              ValidationRulesApiActions.deleteFieldRuleError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  // ---- cross-field mutations ---------------------------------------

  createCrossFieldRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.CreateCrossFieldRule),
      mergeMap(({ payload }) =>
        this.service.createCrossFieldRule(payload).pipe(
          tap(() => this.toast.show('Cross-field rule created', 'success')),
          map((rule) => ValidationRulesApiActions.createCrossFieldRuleSuccess({ payload: rule })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to create cross-field rule');
            return of(
              ValidationRulesApiActions.createCrossFieldRuleError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  patchCrossFieldRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.PatchCrossFieldRule),
      mergeMap(({ id, changes }) =>
        this.service.patchCrossFieldRule(id, changes).pipe(
          tap(() => this.toast.show('Cross-field rule updated', 'success')),
          map((rule) => ValidationRulesApiActions.patchCrossFieldRuleSuccess({ payload: rule })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to update cross-field rule');
            return of(
              ValidationRulesApiActions.patchCrossFieldRuleError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  deleteCrossFieldRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.DeleteCrossFieldRule),
      mergeMap(({ id }) =>
        this.service.deleteCrossFieldRule(id).pipe(
          tap(() => this.toast.show('Cross-field rule deleted', 'success')),
          map(() => ValidationRulesApiActions.deleteCrossFieldRuleSuccess({ id })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to delete cross-field rule');
            return of(
              ValidationRulesApiActions.deleteCrossFieldRuleError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  // ---- configurable catalog: operations + rule type defs ---------

  loadOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.LoadOperations),
      mergeMap(() =>
        this.service.getOperations().pipe(
          map((payload) => ValidationRulesApiActions.loadOperationsSuccess({ payload })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to load operations');
            return of(
              ValidationRulesApiActions.loadOperationsError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  loadRuleTypeDefs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.LoadRuleTypeDefinitions),
      mergeMap(() =>
        this.service.getRuleTypeDefinitions().pipe(
          map((payload) => ValidationRulesApiActions.loadRuleTypeDefsSuccess({ payload })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to load rule type definitions');
            return of(
              ValidationRulesApiActions.loadRuleTypeDefsError({ errors: extractDjangoErrors(err) }),
            );
          }),
        ),
      ),
    ),
  );

  createRuleTypeDef$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.CreateRuleTypeDefinition),
      mergeMap(({ payload }) =>
        this.service.createRuleTypeDefinition(payload).pipe(
          tap(() => this.toast.show('Rule type definition created', 'success')),
          map((rule) => ValidationRulesApiActions.createRuleTypeDefSuccess({ payload: rule })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to create rule type definition');
            return of(
              ValidationRulesApiActions.createRuleTypeDefError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  patchRuleTypeDef$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.PatchRuleTypeDefinition),
      mergeMap(({ id, changes }) =>
        this.service.patchRuleTypeDefinition(id, changes).pipe(
          tap(() => this.toast.show('Rule type definition updated', 'success')),
          map((rule) => ValidationRulesApiActions.patchRuleTypeDefSuccess({ payload: rule })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to update rule type definition');
            return of(
              ValidationRulesApiActions.patchRuleTypeDefError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  deleteRuleTypeDef$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.DeleteRuleTypeDefinition),
      mergeMap(({ id }) =>
        this.service.deleteRuleTypeDefinition(id).pipe(
          tap(() => this.toast.show('Rule type definition deleted', 'success')),
          map(() => ValidationRulesApiActions.deleteRuleTypeDefSuccess({ id })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to delete rule type definition');
            return of(
              ValidationRulesApiActions.deleteRuleTypeDefError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  createParamDef$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.CreateParamDefinition),
      mergeMap(({ payload }) =>
        this.service.createParamDefinition(payload).pipe(
          tap(() => this.toast.show('Param added', 'success')),
          map((p) => ValidationRulesApiActions.createParamDefSuccess({ payload: p })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to add param');
            return of(
              ValidationRulesApiActions.createParamDefError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  patchParamDef$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.PatchParamDefinition),
      mergeMap(({ id, changes }) =>
        this.service.patchParamDefinition(id, changes).pipe(
          tap(() => this.toast.show('Param updated', 'success')),
          map((p) => ValidationRulesApiActions.patchParamDefSuccess({ payload: p })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to update param');
            return of(
              ValidationRulesApiActions.patchParamDefError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );

  deleteParamDef$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ValidationRulesUiActions.DeleteParamDefinition),
      mergeMap(({ id }) =>
        this.service.deleteParamDefinition(id).pipe(
          tap(() => this.toast.show('Param removed', 'success')),
          map(() => ValidationRulesApiActions.deleteParamDefSuccess({ id })),
          catchError((err) => {
            toastFor(this.toast, err, 'Failed to remove param');
            return of(
              ValidationRulesApiActions.deleteParamDefError({
                errors: extractDjangoErrors(err),
              }),
            );
          }),
        ),
      ),
    ),
  );
}
